import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import { NoteText } from './NoteText';
import { mentionApi, markMentionRead } from './api';
import { Comments } from '@/components/runner/Comments';
import { getTeamConversationForCustomer } from '@/actions/teams';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MobileBackButton, MobileHeader } from '@/components/mobile';
function MentionContent({ item }: { item: any }) {
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [ready, setReady] = useState(item.source === 'inline');
  useEffect(() => {
    let active = true;
    const open = async () => {
      if (item.source === 'notepad' && !item.unavailable) {
        const result: any = await dispatch(
          getTeamConversationForCustomer({ _customer: item._customer }) as any,
        );
        if (!active) return;
        if (result?.error) {
          setError('Could not load the notepad. Please reopen this mention to retry.');
          return;
        }
      }
      if (!active) return;
      setReady(true);
      if (!item.unavailable)
        markMentionRead(item._id).catch(() =>
          setError('The note opened, but could not be marked read.'),
        );
    };
    open();
    return () => {
      active = false;
    };
  }, [item._id, item._customer, item.source, item.unavailable, dispatch]);
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {item.actorName} · {item.businessName} · {moment(item.createdAt).format('lll')}
      </p>
      {item.unavailable ? (
        <p>This note was deleted.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto rounded-lg bg-amber-50 p-4 text-sm dark:bg-amber-950/30 max-md:max-h-[30dvh] max-md:text-[15px]">
          <NoteText text={item.note.text} mentions={item.note.mentions} />
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {ready && item.source === 'notepad' && !item.unavailable && (
        <Comments
          // phones: tall enough to read and reply, and it shrinks with the keyboard
          className="h-[45vh] min-h-48 max-md:h-[calc(var(--app-height)*0.55)] max-md:min-h-64"
          isCollapsed={false}
          onToggleCollapse={() => {}}
          isActivityOverview
          showHeader={false}
        />
      )}
    </div>
  );
}
export default function MentionDestination() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<any>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    axios
      .get(mentionApi(`/${encodeURIComponent(id || '')}`), { withCredentials: true })
      .then(({ data }) => {
        if (!active) return;
        if (data._customerConversation && !data.unavailable)
          navigate(
            `/inbox/conversation/${data._customerConversation}?_location=${data._business}&_mention=${data._id}`,
            { replace: true },
          );
        else setItem(data);
      })
      .catch((error) => {
        if (active) setError(error.response?.data?.message || 'Could not open this mention.');
      });
    return () => {
      active = false;
    };
  }, [id, navigate]);
  const title = item ? `Notes for ${item.customerName}` : 'Opening mention…';
  return (
    <main className="h-full overflow-y-auto p-6 max-md:overscroll-contain max-md:p-0 max-md:pb-[max(1rem,var(--safe-bottom))]">
      <MobileHeader
        title={item || !error ? title : 'Mention'}
        left={<MobileBackButton fallback="/mentions" label="Mentions" />}
      />
      <Button variant="link" className="max-md:hidden" onClick={() => navigate('/mentions')}>
        Back to mentions
      </Button>
      <h1 className="my-4 text-xl font-semibold max-md:sr-only">{title}</h1>
      <div className="max-md:px-4 max-md:pt-4">
        {error && <p role="alert">{error}</p>}
        {item && <MentionContent item={item} />}
      </div>
    </main>
  );
}
// Mounted beside the conversation, so the highlighted note also works when it is older than loaded messages.
export function MentionContext() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeConversation = useSelector((state: any) => state.webchat?.activeConversation);
  const id = new URLSearchParams(location.search).get('_mention');
  const [item, setItem] = useState<any>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setItem(null);
    setError('');
    if (!id) return;
    const controller = new AbortController();
    axios
      .get(mentionApi(`/${encodeURIComponent(id)}`), {
        signal: controller.signal,
        withCredentials: true,
      })
      .then(({ data }) => setItem(data))
      .catch((error) => {
        if (!controller.signal.aborted)
          setError(error.response?.data?.message || 'Could not open mention.');
      });
    return () => controller.abort();
  }, [id]);
  const close = () => {
    const query = new URLSearchParams(location.search);
    query.delete('_mention');
    navigate(`${location.pathname}?${query}`, { replace: true });
  };
  if (!id || (!error && (!item || activeConversation?._id !== item._customerConversation)))
    return null;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent mobileVariant="fullscreen" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {item ? `Note for ${item.customerName}` : 'Mention unavailable'}
          </DialogTitle>
        </DialogHeader>
        {error ? <p role="alert">{error}</p> : <MentionContent item={item} />}
      </DialogContent>
    </Dialog>
  );
}
